// The app's own file format: the Tree as JSON with a small envelope.
// Lossless (unlike GEDCOM), human-readable, and easy for other tools to consume.

import type { FileBag } from '../model/files';
import { newId } from '../model/people';
import type { Tree } from '../model/types';

export const NATIVE_FORMAT = 'relationships-tree';
/** 2 added sources, media, to-dos, and the media files themselves. */
export const NATIVE_VERSION = 2;
export const NATIVE_EXTENSION = '.relationships.json';

export interface NativeFile {
  format: typeof NATIVE_FORMAT;
  version: number;
  exportedAt: string;
  tree: Tree;
  /** Media files by key, as data URLs. */
  files?: Record<string, string>;
}

/** `files` holds the media files as data URLs (see blobToDataUrl), so the backup is complete. */
export function exportNative(tree: Tree, files?: Record<string, string>): string {
  const file: NativeFile = { format: NATIVE_FORMAT, version: NATIVE_VERSION, exportedAt: new Date().toISOString(), tree };
  if (files && Object.keys(files).length) file.files = files;
  return JSON.stringify(file, null, 2);
}

export async function blobToDataUrl(blob: Blob): Promise<string> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let bin = '';
  for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return `data:${blob.type || 'application/octet-stream'};base64,${btoa(bin)}`;
}

export function dataUrlToBlob(url: string): Blob | undefined {
  const m = url.match(/^data:([^;,]*)(;base64)?,(.*)$/s);
  if (!m) return undefined;
  if (!m[2]) return new Blob([decodeURIComponent(m[3])], { type: m[1] });
  const bin = atob(m[3]);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: m[1] });
}

export function looksNative(text: string): boolean {
  return /^\s*\{/.test(text) && text.includes(`"${NATIVE_FORMAT}"`);
}

/** Parse and validate a native file. Throws a user-readable Error on bad input. */
export function importNative(text: string): Tree {
  return importNativeFile(text).tree;
}

/** A native file's tree and the media files that came with it. */
export function importNativeFile(text: string): { tree: Tree; files: FileBag } {
  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch {
    throw new Error("This file isn't valid JSON.");
  }
  const f = data as Partial<NativeFile>;
  if (!f || f.format !== NATIVE_FORMAT || !f.tree) throw new Error("This JSON file isn't a Relationships tree.");
  if (typeof f.version !== 'number' || f.version > NATIVE_VERSION)
    throw new Error('This file was made by a newer version of Relationships. Please update and try again.');
  const t = f.tree;
  if (typeof t.people !== 'object' || typeof t.relationships !== 'object')
    throw new Error('This Relationships file is missing people or relationships.');
  const files: FileBag = new Map();
  for (const [key, url] of Object.entries(f.files ?? {})) {
    const blob = typeof url === 'string' ? dataUrlToBlob(url) : undefined;
    if (blob) files.set(key, blob);
  }
  const tree = migrate({
    ...t,
    groups: t.groups ?? {},
    // Give the import its own id so it never overwrites an existing tree in storage.
    id: newId('t'),
  });
  return { tree, files };
}

/**
 * Upgrade older trees in place: native files on import, and trees saved in this browser when
 * they're opened. Add a step whenever the model changes.
 */
export function migrate(t: Tree): Tree {
  t.groups ??= {};
  t.sources ??= {};
  t.media ??= {};
  t.todos ??= {};
  for (const p of Object.values(t.people)) {
    p.events ??= [];
    p.tags ??= [];
    p.notes ??= '';
    p.names ??= [{ given: '', surname: '', type: 'name' }];
    // "Nickname" used to be a kind of name as well as a field; a whole other name is "Also known as".
    for (const n of p.names) if ((n.type as string) === 'nickname') n.type = 'alias';
    p.gender ??= { identity: 'unknown' };
    if (p.living === undefined) p.living = null;
  }
  // "Chosen family" used to be a kind of friendship. Chosen family is now a parent or sibling
  // type, and a tie between peers is a chosen sibling (unless they're already linked as siblings).
  const pair = (a: string, b: string) => [a, b].sort().join('|');
  const siblings = new Set(Object.values(t.relationships).filter((r) => r.kind === 'sibling').map((r) => pair(r.a, r.b)));
  for (const [id, r] of Object.entries(t.relationships)) {
    if (r.kind === 'social' && (r.type as string) === 'chosen-family') {
      const { a, b, start, end, place, notes, label, citations } = r;
      if (siblings.has(pair(a, b))) {
        delete t.relationships[id];
        continue;
      }
      t.relationships[id] = { id, kind: 'sibling', type: 'chosen', a, b, start, end, place, notes, label, citations };
      siblings.add(pair(a, b));
    }
  }
  return t;
}
